import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {Router} from '@angular/router';
import {
  ExamReportsClient,
  SelectListResponse,
  StudentResponse,
  StudentsClient,
} from '../../../../generated-client/generated-api-client';
import {Observable} from 'rxjs';
import {dateTimeIsFutureValidator} from '../../../../custom-validators/dateTimeIsFutureValidator';
import {ErrorProcessorService} from '../../../../services/error-processor.service';
import {DateTime} from 'luxon';
import flatpickr from 'flatpickr';
import {ValidatorPatterns} from '../../../../custom-validators/validatorPatterns';
import Options = flatpickr.Options.Options;
import {
  isFormModifiedDataService,
  SimpleNavigationService
} from "../../../../services/data-services/navigation-data-services";
import {Location} from "@angular/common";
import {StudentExamDataService} from "../../../../services/data-services/student-exam-data.service";

@Component({
  selector: 'app-student-form',
  templateUrl: './student-form.component.html',
  styleUrls: ['./student-form.component.sass'],
})
export class StudentFormComponent implements OnInit {
  @Input() response!: Observable<StudentResponse>;
  @Input() examReportId!: string;
  @Input() studentId!: string;
  @Output() onSubmitCallback: EventEmitter<any> = new EventEmitter();

  studentForm!: FormGroup;
  isLoading: boolean = true;
  languageSelectList!: SelectListResponse;

  examDateFlatpickrOptions: Options = {
    maxDate: undefined,
  };

  constructor(
    private router: Router,
    private studentsClient: StudentsClient,
    private errorProcessorService: ErrorProcessorService,
    private _examReportsClient: ExamReportsClient,
    private _isFormModifiedDataService: isFormModifiedDataService,
    private _navigationService: SimpleNavigationService,
    private _studentExamDataService: StudentExamDataService,
  ) { }

  ngOnInit(): void {
    this.response?.subscribe(
      next => {
        this.studentsClient.languages().subscribe(
          response => {
            this.languageSelectList = response;
            this.setForm(next);
          },
          error => this.errorProcessorService.processError(error, false, true)
        );
      },
      error => this.errorProcessorService.processError(error, false, true)
    );

    if (!this.response) {
      this.studentsClient.languages().subscribe(
        response => {
          this.languageSelectList = response;
          this.setForm();
        },
        error => this.errorProcessorService.processError(error, true)
      );
    }
  }

  get registrationNumber() {
    return this.studentForm.get('registrationNumber')!;
  }

  get title() {
    return this.studentForm.get('title')!;
  }

  get surname() {
    return this.studentForm.get('surname')!;
  }

  get forename() {
    return this.studentForm.get('forename')!;
  }

  get forename2() {
    return this.studentForm.get('forename2')!;
  }

  get citizenship() {
    return this.studentForm.get('citizenship')!;
  }

  get mothersname() {
    return this.studentForm.get('mothersName')!;
  }

  get birthplace() {
    return this.studentForm.get('birthplace')!;
  }

  get birthdate() {
    return this.studentForm.get('birthdate')!;
  }

  get birthName() {
    return this.studentForm.get('birthName')!;
  }

  get email() {
    return this.studentForm.get('email')!;
  }

  get language() {
    return this.studentForm.get('language')!;
  }

  get computerNumber() {
    return this.studentForm.get('computerNumber')!;
  }

  get examStartedAtUtc() {
    return this.studentForm.get('examStartedAtUtc')!;
  }

  get twoFactorEnabled() {
    return this.studentForm.get('twoFactorEnabled')!;
  }

  onSubmit(): void {
    this.onSubmitCallback.emit([this.studentForm]);
  }

  onGoBack() {
    if (this.studentId)
      this._navigationService.navigateTo(`/exam-reports/${this.examReportId}/students/${this.studentId}`);
    else
      this._navigationService.navigateTo(`/exam-reports/${this.examReportId}/students`);
  }

  setForm(response?: StudentResponse): void {
    this._examReportsClient.get(this.examReportId).subscribe(examReport => {
      this.studentForm = new FormGroup({
        registrationNumber: new FormControl(response?.registrationNumber),
        title: new FormControl(response?.title),
        surname: new FormControl(response?.surname, [Validators.required]),
        forename: new FormControl(response?.forename1, [Validators.required]),
        forename2: new FormControl(response?.forename2),
        citizenship: new FormControl(response?.citizenship),
        mothersName: new FormControl(response?.mothersName),
        birthplace: new FormControl(response?.birthPlace, [Validators.required]),
        birthdate: new FormControl(response?.birthDate, [Validators.required, dateTimeIsFutureValidator()]),
        birthName: new FormControl(response?.birthName),
        email: new FormControl(response?.email, [Validators.pattern(ValidatorPatterns.email), Validators.required]),
        language: new FormControl(response?.language, [Validators.required]),
        computerNumber: new FormControl(response?.computerNumber, [Validators.required, Validators.min(0)]),
        examStartedAtUtc: new FormControl(response?.examStartedAtUtc ?? examReport.examStartedAtUtc, [
          Validators.required,
        ]),
      });
      this.examDateFlatpickrOptions = {
        maxDate: DateTime.fromISO(examReport.examEndedAtUtc.toString()).toJSDate(),
      };
      this.errorProcessorService.formGroup = this.studentForm;
      this.isLoading = false;
      this.studentForm.valueChanges.subscribe(() => {
        this._isFormModifiedDataService.isFormModified = true;
      });
    });
  }

}
